import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import {
  Loader2, Lock, CheckCircle2, XCircle, CreditCard, QrCode, Barcode,
  Copy, ExternalLink, ShieldCheck, SplitSquareHorizontal, AlertCircle,
} from 'lucide-react';
import { API_URL } from '@/contexts/AuthContext';
import { tokenizeCard } from '@/lib/pagarmeToken';
import { detectBrand, formatCardNumber, BrandMark } from '@/lib/cardBrand';
import { formatBRL } from '@/lib/format';

const POLL_INTERVAL_MS = 5000;

const parcelaOptions = (totalValue, maxParcelas) =>
  Array.from({ length: Math.max(1, maxParcelas || 1) }, (_, i) => {
    const n = i + 1;
    return { n, label: `${n}x de ${formatBRL(totalValue / n)}` };
  });

const emptyCard = { number: '', holder_name: '', exp_month: '', exp_year: '', cvv: '' };

const payErrorMessage = (err) => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 402) return detail || 'Pagamento recusado, tente outro cartão';
  if (status === 409) return detail || 'Este pedido já foi pago';
  if (status === 400) return detail || 'Dados de pagamento inválidos';
  return detail || 'Não foi possível processar o pagamento. Tente novamente.';
};

// ── Design tokens (dark premium, tintado pro azul da marca) ──────────────────
// Página pública standalone: cores fixas (não dependem do tema do app).
const FIELD_BASE =
  'w-full rounded-lg bg-[#131c2e] text-slate-100 placeholder:text-slate-500 ' +
  'border border-white/10 px-3.5 py-3 text-[15px] outline-none transition ' +
  'shadow-[inset_0_1px_2px_rgba(0,0,0,0.35)] ' +
  'focus:border-blue-500/70 focus:ring-4 focus:ring-blue-500/20 ' +
  'motion-reduce:transition-none';
const FIELD_LABEL = 'block text-xs font-medium text-slate-400 mb-1.5';

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={FIELD_LABEL}>{label}</label>
      {children}
    </div>
  );
}

function CardFields({ card, onChange, idPrefix }) {
  const set = (field) => (e) => onChange({ ...card, [field]: e.target.value });
  const brand = detectBrand(card.number);
  return (
    <div className="space-y-3.5">
      <Field label="Número do cartão" htmlFor={`${idPrefix}-number`}>
        <div className="relative">
          <input
            id={`${idPrefix}-number`}
            inputMode="numeric"
            autoComplete="cc-number"
            placeholder="0000 0000 0000 0000"
            className={`${FIELD_BASE} pr-14 tracking-[0.02em]`}
            value={formatCardNumber(card.number)}
            onChange={(e) => onChange({ ...card, number: e.target.value.replace(/\D/g, '').slice(0, 19) })}
          />
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2">
            {brand ? (
              <BrandMark brand={brand} className="h-6 w-auto rounded-[4px] shadow-sm" />
            ) : (
              <CreditCard className="h-4 w-4 text-slate-600" />
            )}
          </span>
        </div>
      </Field>

      <Field label="Nome impresso no cartão" htmlFor={`${idPrefix}-name`}>
        <input
          id={`${idPrefix}-name`}
          placeholder="Como está no cartão"
          autoComplete="cc-name"
          className={FIELD_BASE}
          value={card.holder_name}
          onChange={set('holder_name')}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Validade" htmlFor={`${idPrefix}-exp`}>
          <div className="flex items-center gap-2">
            <input
              id={`${idPrefix}-exp`}
              inputMode="numeric"
              placeholder="MM"
              maxLength={2}
              className={`${FIELD_BASE} text-center`}
              value={card.exp_month}
              onChange={set('exp_month')}
            />
            <span className="text-slate-600">/</span>
            <input
              inputMode="numeric"
              placeholder="AA"
              maxLength={2}
              className={`${FIELD_BASE} text-center`}
              value={card.exp_year}
              onChange={set('exp_year')}
            />
          </div>
        </Field>
        <Field label="CVV" htmlFor={`${idPrefix}-cvv`}>
          <div className="relative">
            <input
              id={`${idPrefix}-cvv`}
              inputMode="numeric"
              placeholder="123"
              maxLength={4}
              className={`${FIELD_BASE} pr-9`}
              value={card.cvv}
              onChange={set('cvv')}
            />
            <Lock className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
          </div>
        </Field>
      </div>
    </div>
  );
}

// Botão primário confiante (azul da marca) com estado de loading.
function PayButton({ children, loading, disabled, ...props }) {
  return (
    <button
      className="group inline-flex w-full items-center justify-center gap-2 rounded-lg
        bg-blue-600 px-5 py-3.5 text-[15px] font-semibold text-white
        shadow-[0_8px_24px_-8px_rgba(37,99,235,0.7)] transition
        hover:bg-blue-500 active:translate-y-px active:bg-blue-600
        disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none
        focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/30
        motion-reduce:transition-none motion-reduce:active:translate-y-0"
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

// Botão secundário (copiar código / linha digitável).
function GhostButton({ children, ...props }) {
  return (
    <button
      className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/12
        bg-white/[0.03] px-4 py-2.5 text-sm font-medium text-slate-200 transition
        hover:border-white/25 hover:bg-white/[0.06]
        focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/20
        motion-reduce:transition-none"
      {...props}
    >
      {children}
    </button>
  );
}

const METHOD_META = {
  credit_card: { icon: CreditCard },
  pix: { label: 'PIX', icon: QrCode },
  boleto: { label: 'Boleto', icon: Barcode },
};

// Segmented control com indicador deslizante.
function MethodSwitcher({ metodos, method, split, onSelect }) {
  const activeIndex = Math.max(0, metodos.indexOf(method));
  return (
    <div
      className="relative grid gap-1 rounded-xl border border-white/10 bg-[#0b1220] p-1"
      style={{ gridTemplateColumns: `repeat(${metodos.length}, 1fr)` }}
    >
      <span
        aria-hidden
        className="absolute inset-y-1 rounded-lg bg-blue-600/15 ring-1 ring-inset ring-blue-500/40 transition-transform duration-300 motion-reduce:transition-none"
        style={{
          width: `calc((100% - 0.5rem) / ${metodos.length})`,
          transform: `translateX(calc(${activeIndex} * 100%))`,
          transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      />
      {metodos.map((m) => {
        const meta = METHOD_META[m];
        const label = m === 'credit_card' ? (split ? '2 Cartões' : 'Cartão') : meta.label;
        const active = method === m;
        return (
          <button
            key={m}
            type="button"
            onClick={() => onSelect(m)}
            className={`relative z-10 flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium transition-colors motion-reduce:transition-none ${
              active ? 'text-blue-200' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <meta.icon className="h-4 w-4" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

const Checkout = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cancelado, setCancelado] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [method, setMethod] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paid, setPaid] = useState(false);
  // Pagamento sob análise (antifraude): tela "em processamento" persistente —
  // sobrevive a reload porque o GET /checkout também devolve status em_analise.
  const [analise, setAnalise] = useState(false);
  const analiseRef = useRef(false);

  const [card, setCard] = useState(emptyCard);
  const [parcelas, setParcelas] = useState(1);
  const [cards2, setCards2] = useState([{ ...emptyCard }, { ...emptyCard }]);
  const [parcelas2, setParcelas2] = useState([1, 1]);

  const [pix, setPix] = useState(null);
  const [boleto, setBoleto] = useState(null);
  const pollRef = useRef(null);

  const fetchCheckout = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/checkout/${token}`);
      setCheckout(data);
      setParcelas(1);
      setParcelas2([1, 1]);
      if (data.status === 'pago') setPaid(true);
      if (data.status === 'em_analise') setAnalise(true);
      if (data.status === 'cancelado') setCancelado(true); // estornado/cancelado: o link não cobra mais
      if (data.boleto) setBoleto(data.boleto); // boleto já emitido: mostra o mesmo, não gera outro
      if (!method) setMethod(data.metodos?.[0] || 'credit_card');
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
      else toast.error('Não foi possível carregar o checkout. Tente recarregar a página.');
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint: método default é setado só na 1ª carga, não precisa de 'method' nas deps

  useEffect(() => { fetchCheckout(); }, [fetchCheckout]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/checkout/${token}/status`);
        if (data.parte_paga === true || data.payment_status === 'confirmed' || data.payment_status === 'paid') {
          setPaid(true);
          setAnalise(false);
          stopPolling();
        } else if (analiseRef.current && data.em_analise === false) {
          // Análise resolveu sem aprovar (recusa/TTL): libera o formulário de novo.
          setAnalise(false);
          stopPolling();
          toast.error('O pagamento não foi aprovado. Você pode tentar novamente.');
        }
      } catch {
        // ponytail: silêncio na falha de polling — próximo tick tenta de novo
      }
    }, POLL_INTERVAL_MS);
  }, [token, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => { analiseRef.current = analise; }, [analise]);

  // Em análise (inclusive ao carregar a página já nesse estado): fica pollando
  // até o webhook confirmar (pago) ou a análise soltar (volta ao formulário).
  useEffect(() => {
    if (analise && !paid) startPolling();
  }, [analise, paid, startPolling]);

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  const isCardMethod = method === 'credit_card';

  // Tokeniza direto na API /tokens (mesmo endpoint que o tokenizecard.js usa
  // por baixo) e paga — mesmo padrão do pay2Cards, com erro real no toast.
  const paySingleCard = async () => {
    setProcessing(true);
    try {
      const card_token = await tokenizeCard(checkout.pagarme_public_key, card);
      const { data } = await axios.post(`${API_URL}/checkout/${token}/pay`, {
        metodo: 'credit_card',
        card_token,
        parcelas,
      });
      if (data?.status === 'pendente') {
        // Antifraude em análise: não é recusa — tela persistente "em
        // processamento" (o effect começa o poll; sobrevive a reload).
        setAnalise(true);
      } else {
        setPaid(true);
        toast.success('Pagamento aprovado!');
      }
    } catch (err) {
      toast.error(err?.response ? payErrorMessage(err) : err.message);
    } finally {
      setProcessing(false);
    }
  };

  const pay2Cards = async () => {
    setProcessing(true);
    try {
      const tokens = await Promise.all(
        cards2.map((c) => tokenizeCard(checkout.pagarme_public_key, c))
      );
      const { data } = await axios.post(`${API_URL}/checkout/${token}/pay`, {
        metodo: 'credit_card_2x',
        cartoes: tokens.map((card_token, i) => ({ card_token, parcelas: parcelas2[i] })),
      });
      if (data?.status === 'pendente') {
        setAnalise(true);
      } else {
        setPaid(true);
        toast.success('Pagamento aprovado!');
      }
    } catch (err) {
      toast.error(err?.response ? payErrorMessage(err) : err.message);
    } finally {
      setProcessing(false);
    }
  };

  const payPix = async () => {
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API_URL}/checkout/${token}/pay`, { metodo: 'pix' });
      setPix(data.pix);
      startPolling();
    } catch (err) {
      toast.error(err?.response ? payErrorMessage(err) : err.message);
    } finally {
      setProcessing(false);
    }
  };

  const payBoleto = async () => {
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API_URL}/checkout/${token}/pay`, { metodo: 'boleto' });
      setBoleto(data.boleto);
      startPolling();
    } catch (err) {
      toast.error(err?.response ? payErrorMessage(err) : err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <PageShell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-blue-500" />
        </div>
      </PageShell>
    );
  }

  if (notFound) {
    return (
      <PageShell narrow>
        <Panel className="p-9 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10">
            <XCircle className="h-6 w-6 text-slate-400" />
          </div>
          <h1 className="text-lg font-semibold text-slate-100">Checkout não encontrado</h1>
          <p className="mt-2 text-sm text-slate-400">
            Este link de pagamento é inválido ou expirou.
          </p>
        </Panel>
      </PageShell>
    );
  }

  if (cancelado) {
    return (
      <PageShell narrow>
        <Panel className="p-10 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10">
            <XCircle className="h-6 w-6 text-slate-400" />
          </div>
          <h1 className="text-lg font-semibold text-slate-100">Pedido cancelado</h1>
          <p className="mt-2 text-sm text-slate-400">
            Este pedido foi cancelado e o link de pagamento não está mais ativo. Em caso de dúvida, fale com seu consultor.
          </p>
        </Panel>
      </PageShell>
    );
  }

  if (paid) {
    return (
      <PageShell narrow>
        <Panel className="p-10 text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 ring-1 ring-emerald-400/30">
            <CheckCircle2 className="h-9 w-9 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-50">Pagamento aprovado</h1>
          <p className="mt-2.5 text-sm leading-relaxed text-slate-400">
            Obrigado{checkout?.cliente_primeiro_nome ? `, ${checkout.cliente_primeiro_nome}` : ''}. Seu pedido de{' '}
            <span className="font-semibold text-slate-200">{formatBRL(checkout?.valor_total)}</span> foi confirmado.
          </p>
        </Panel>
      </PageShell>
    );
  }

  if (analise) {
    return (
      <PageShell narrow>
        <Panel className="p-10 text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-blue-500/10 ring-1 ring-blue-400/30">
            <Loader2 className="h-9 w-9 animate-spin text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-50">Pagamento em processamento</h1>
          <p className="mt-2.5 text-sm leading-relaxed text-slate-400">
            Estamos confirmando seu pagamento de{' '}
            <span className="font-semibold text-slate-200">{formatBRL(checkout?.valor_total)}</span> com o banco.
            Esta página atualiza sozinha,{' '}
            <span className="font-semibold text-slate-200">não é necessário pagar novamente</span>.
          </p>
        </Panel>
      </PageShell>
    );
  }

  // Falha de rede/API que não seja 404 deixa `checkout` nulo: sem este guard o
  // render principal estourava em `checkout.cliente_primeiro_nome` e o cliente
  // via "Algo deu errado" em vez de uma mensagem com opção de tentar de novo.
  if (!checkout) {
    return (
      <PageShell narrow>
        <Panel className="p-10 text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10 ring-1 ring-amber-400/30">
            <AlertCircle className="h-9 w-9 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-50">Não foi possível carregar</h1>
          <p className="mt-2.5 text-sm leading-relaxed text-slate-400">
            Sua conexão pode ter oscilado. Nenhuma cobrança foi feita. Tente novamente.
          </p>
          <button
            type="button"
            onClick={() => { setLoading(true); fetchCheckout(); }}
            className="mt-6 h-11 rounded-xl bg-blue-600 px-6 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
          >
            Tentar novamente
          </button>
        </Panel>
      </PageShell>
    );
  }

  const split = checkout?.split;
  // Página de pagamento não pode quebrar por payload inesperado: um contrato
  // fora do previsto (ex.: bundle antigo em cache falando com a API nova)
  // degrada pro cartão em vez de derrubar a tela no ErrorBoundary.
  const metodos = Array.isArray(checkout?.metodos) && checkout.metodos.length
    ? checkout.metodos
    : ['credit_card'];

  return (
    <PageShell>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:gap-10">
        {/* ── ESQUERDA: marca + contexto + valor + confiança ── */}
        <aside className="lg:pt-4">
          <img src="/logo-bcmed.png" alt="BCMED" className="mb-8 h-11 w-auto object-contain" />

          <p className="text-lg font-medium text-slate-300">Olá, {checkout.cliente_primeiro_nome}</p>
          {checkout.parte && (
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-blue-400/25 bg-blue-500/10 px-2.5 py-1 text-xs font-medium text-blue-300">
              <SplitSquareHorizontal className="h-3.5 w-3.5" />
              Pagamento {checkout.parte.indice} de {checkout.parte.total_partes}
            </span>
          )}
          <p className="mt-1 text-sm text-slate-500">Valor a pagar</p>
          <p className="mt-1 text-[2.75rem] font-bold leading-none tracking-tight text-slate-50 sm:text-5xl">
            {formatBRL(checkout.valor_total)}
          </p>

          <div className="mt-7 flex items-center gap-2.5 text-sm text-slate-300">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            <span className="font-medium">Pagamento seguro</span>
          </div>
          <p className="mt-2 max-w-xs text-[13px] leading-relaxed text-slate-500">
            Ambiente protegido. Seus dados são criptografados e não ficam armazenados conosco.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {['visa', 'mastercard', 'elo', 'amex'].map((b) => (
              <BrandMark key={b} brand={b} className="h-6 w-auto rounded-[4px] opacity-90" />
            ))}
          </div>
        </aside>

        {/* ── DIREITA: superfície de pagamento ── */}
        <div className="space-y-4">
          {metodos.length > 1 && (
            <MethodSwitcher metodos={metodos} method={method} split={split} onSelect={setMethod} />
          )}

          <Panel className="p-6 sm:p-7">
            {isCardMethod && !split && (
              <div className="space-y-5">
                <CardFields card={card} onChange={setCard} idPrefix="card" />
                {/* <select> NATIVO de propósito: no celular abre o picker do
                    próprio sistema — sem portal/posicionamento JS (a superfície
                    que quebrava pra alguns clientes ao escolher parcelas). */}
                <Field label="Parcelas" htmlFor="parcelas">
                  <select
                    id="parcelas"
                    value={String(parcelas)}
                    onChange={(e) => setParcelas(Number(e.target.value))}
                    className="h-12 w-full rounded-lg border border-white/10 bg-[#131c2e] px-3 text-base text-slate-100 outline-none transition-colors focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/20"
                  >
                    {parcelaOptions(checkout.valor_total, checkout.max_parcelas).map((opt) => (
                      <option key={opt.n} value={opt.n}>{opt.label}</option>
                    ))}
                  </select>
                </Field>
                <PayButton loading={processing} onClick={paySingleCard}>
                  Pagar {formatBRL(checkout.valor_total)}
                </PayButton>
              </div>
            )}

            {isCardMethod && split && (
              <div className="space-y-6">
                {split.map((s, i) => (
                  <div key={s.indice} className="space-y-3.5 border-b border-white/10 pb-6 last:border-0 last:pb-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-slate-200">Cartão {i + 1}</h3>
                      <span className="rounded-md bg-blue-500/10 px-2 py-0.5 text-sm font-semibold text-blue-300">
                        {formatBRL(s.valor)}
                      </span>
                    </div>
                    <CardFields
                      card={cards2[i]}
                      onChange={(c) => setCards2((prev) => prev.map((p, idx) => (idx === i ? c : p)))}
                      idPrefix={`card2-${i}`}
                    />
                    <Field label="Parcelas" htmlFor={`parcelas2-${i}`}>
                      <select
                        id={`parcelas2-${i}`}
                        value={String(parcelas2[i])}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setParcelas2((prev) => prev.map((p, idx) => (idx === i ? v : p)));
                        }}
                        className="h-12 w-full rounded-lg border border-white/10 bg-[#131c2e] px-3 text-base text-slate-100 outline-none transition-colors focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/20"
                      >
                        {parcelaOptions(s.valor, s.max_parcelas).map((opt) => (
                          <option key={opt.n} value={opt.n}>{opt.label}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                ))}
                <PayButton loading={processing} onClick={pay2Cards}>
                  Pagar {formatBRL(checkout.valor_total)}
                </PayButton>
              </div>
            )}

            {method === 'pix' && (
              <div className="space-y-4 text-center">
                {!pix ? (
                  <>
                    <p className="text-sm leading-relaxed text-slate-400">
                      Gere o código PIX e pague pelo app do seu banco. A confirmação é imediata.
                    </p>
                    <PayButton loading={processing} onClick={payPix}>Gerar PIX</PayButton>
                  </>
                ) : (
                  <>
                    {pix.qr_code ? (
                      // QR gerado no navegador a partir da string copia-e-cola (não depende
                      // do qr_code_url da Pagar.me). Fundo branco p/ ser escaneável no tema escuro.
                      <div className="mx-auto flex h-56 w-56 items-center justify-center rounded-2xl bg-white p-4 shadow-[0_12px_40px_-12px_rgba(0,0,0,0.6)] ring-1 ring-white/10">
                        <QRCodeSVG value={pix.qr_code} size={192} />
                      </div>
                    ) : pix.qr_code_url ? (
                      <img src={pix.qr_code_url} alt="QR Code PIX" className="mx-auto h-56 w-56 rounded-2xl bg-white p-4 ring-1 ring-white/10" />
                    ) : null}
                    <GhostButton onClick={() => copyToClipboard(pix.qr_code, 'Código PIX')}>
                      <Copy className="h-4 w-4" /> Copiar código
                    </GhostButton>
                    <p className="flex items-center justify-center gap-2 text-sm text-slate-400">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                      Escaneie ou copie o código. Confirmamos assim que o PIX cair.
                    </p>
                  </>
                )}
              </div>
            )}

            {method === 'boleto' && (
              <div className="space-y-4 text-center">
                {!boleto ? (
                  <>
                    <p className="text-sm leading-relaxed text-slate-400">
                      Gere o boleto e pague em qualquer banco ou app. A compensação leva até 2 dias úteis.
                    </p>
                    <PayButton loading={processing} onClick={payBoleto}>Gerar boleto</PayButton>
                  </>
                ) : (
                  <>
                    <p className="break-all rounded-lg border border-white/10 bg-[#0b1220] p-3.5 font-mono text-sm text-slate-200">
                      {boleto.linha_digitavel}
                    </p>
                    <GhostButton onClick={() => copyToClipboard(boleto.linha_digitavel, 'Linha digitável')}>
                      <Copy className="h-4 w-4" /> Copiar linha digitável
                    </GhostButton>
                    <a
                      href={boleto.pdf_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 text-sm font-medium text-blue-400 hover:text-blue-300 hover:underline"
                    >
                      <ExternalLink className="h-4 w-4" /> Abrir boleto (PDF)
                    </a>
                    <p className="flex items-center justify-center gap-2 text-sm text-slate-400">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-400" /> Aguardando pagamento…
                    </p>
                  </>
                )}
              </div>
            )}
          </Panel>

          <p className="flex items-center justify-center gap-1.5 text-xs text-slate-600">
            <Lock className="h-3 w-3" /> Conexão segura e criptografada
          </p>
        </div>
      </div>
    </PageShell>
  );
};

// Superfície elevada (um passo mais clara que a página).
function Panel({ children, className = '' }) {
  return (
    <div className={`rounded-2xl border border-white/[0.08] bg-[#0e1524] shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset,0_24px_60px_-30px_rgba(0,0,0,0.9)] ${className}`}>
      {children}
    </div>
  );
}

function PageShell({ children, narrow = false }) {
  return (
    <div className="relative min-h-screen bg-[#070b14] text-slate-100 antialiased [--gray-500:140_147_158]">
      {/* glow radial azul da marca, bem contido */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
        style={{ background: 'radial-gradient(60% 100% at 50% 0%, rgba(37,99,235,0.16), transparent 70%)' }}
      />
      <div className="relative mx-auto flex min-h-screen w-full max-w-[960px] flex-col justify-center px-4 py-10 sm:px-6">
        <div className={narrow ? 'mx-auto w-full max-w-md' : 'w-full'}>
          {narrow && (
            <div className="mb-6 flex flex-col items-center">
              <img src="/logo-bcmed.png" alt="BCMED" className="mb-3 h-11 w-auto object-contain" />
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Lock className="h-3.5 w-3.5 text-emerald-400" /> Pagamento seguro
              </div>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

export default Checkout;
